/**
 * Source allowlists + per-source modal copy for the Step 5 upgrade modal.
 *
 * Two distinct allowlists (per spec design Component 1):
 *   - MODAL_SOURCE_ALLOWLIST — sources that can OPEN the modal. Used by the
 *     deep-link effect in `_layout.tsx` and by the SOURCE_COPY exhaustiveness
 *     test (task 29).
 *   - ANALYTICS_SOURCE_ALLOWLIST — superset including `pricing_page`. Used
 *     only as a documentation reference for analytics dashboards. The
 *     standalone `/pricing` page does NOT open the modal; it sends
 *     `customData.upgrade_source = 'pricing_page'` directly to Paddle.
 *
 * Adding a new source:
 *   1. Add to MODAL_SOURCE_ALLOWLIST (and ANALYTICS_SOURCE_ALLOWLIST if also analytics-only).
 *   2. Add a corresponding SOURCE_COPY entry. The exhaustiveness test will fail otherwise.
 *   3. Wire the trigger surface to call `triggerUpgradeModal({ source: <new> })`.
 */

export const MODAL_SOURCE_ALLOWLIST = [
  // Step 3 cap-CTAs (already wired)
  'blurred_score_factor',
  'dormant_card',
  'dormant_pill',
  'blurred_match_badge',
  'blurred_match_badge_shared',
  'cap_saved_listings',
  'cap_paste_a_link',
  'cap_pdf_report',
  'cap_feed_listings',
  'cap_tracked_models', // monetization-step-6 Phase H task 33
  'undo_banner',
  // Step 5 NEW UI sources
  'verdict_headline',          // RESERVED — UI consumer deferred to future spec (mirrors cap_compare/generate_and_lock_brief reservation pattern; see Step 5 task 14)
  'running_costs_locked',
  // Forward-compat reservations (no UI consumer yet)
  'cap_compare',               // RESERVED — Compare UI deferred per Step 5 Req 15
  'generate_and_lock_brief',   // RESERVED — Negotiation paywall ships in Step 6
  'legacy_model_cap',          // Step 5 server-side cap (persist-car-selection 403)
  // D-018: persistent header CTA sources
  'trial_header_cta',
  'grandfather_header_cta',
  'free_header_cta',
  'cancelled_grace_cta',
  'paused_grace_cta',
  'paused_header_cta',
  // Email + screen + grandfather-email sources
  'weekly_digest',
  'day_before_email',
  'reality_check',
  'car_selector',
  // Grandfather window reduced to 14 days (2026-04-21) with a 5-touch cadence:
  // Day 0 announcement, Day 7 engagement check-in, Day 10 FR urgency,
  // Day 13 final reminder, Day 14 transition.
  'grandfather_email_d0',
  'grandfather_email_d7',
  'grandfather_email_d10',
  'grandfather_email_d13',
  'grandfather_email_d14',
  // D-026 mid-trial aha toasts (one per trigger)
  'trial_aha_listings_viewed_3',
  'trial_aha_dealer_click_1',
  'trial_aha_saves_2',
  'trial_aha_neg_2',
  // D-028: CBP user lands on cancel and chooses to switch to a subscription
  'cbp_cancel_explainer',
  // D-025: CBP tail-window (≤8d) header chip tap
  'cbp_header_chip',
  // J09 trial CTAs (audit H8 — added 2026-04-30 for PostHog attribution)
  'trial_welcome_d0',
  'trial_nudge_d2',
  'trial_midtrial',
  // J10a-e activation CTAs (audit H8 — Pro-already cohort, attribution-only)
  'activation_trial',
  'activation_pastdue',
  'activation_resume',
  'activation_cbp',
  'activation_direct',
  // Negotiation in-chat upgrade triggers (audit C5 — was hitting default copy)
  'negotiation_generate_draft',
  'negotiation_generate_draft_retry',
  'negotiation_handoff',
];

export const ANALYTICS_SOURCE_ALLOWLIST = [
  ...MODAL_SOURCE_ALLOWLIST,
  // Pricing page CTAs send this in customData but DO NOT open the modal
  'pricing_page',
  // Pass B trial-expired email lands directly on /pricing (not /home), so
  // this source never opens the modal — it only flows through PricingSection's
  // PostHog attribution + Paddle customData. Deliberately NOT in
  // MODAL_SOURCE_ALLOWLIST (no SOURCE_COPY entry needed).
  'trial_expired_email',
];

/**
 * Source-specific "why upgrade" copy. Every MODAL_SOURCE_ALLOWLIST entry MUST
 * have a corresponding key here (enforced by the exhaustiveness test).
 *
 * Style: 1 sentence body (~80-120 chars) per Step 5 NFR Usability. Plain
 * language, no em dashes (per project feedback memory).
 */
export const SOURCE_COPY = {
  blurred_score_factor: {
    title: 'See your full match analysis',
    body: 'Pro unlocks the score breakdown for every listing in your feed.',
  },
  dormant_card: {
    title: 'Reactivate this car',
    body: 'Pro lets you track multiple models at once with full alerts and analysis.',
  },
  dormant_pill: {
    title: 'Reactivate this filter',
    body: 'Pro lets you keep all your filters live, not just one at a time.',
  },
  blurred_match_badge: {
    title: 'See how this car matches you',
    body: 'Pro unlocks the match score and reasoning for every listing.',
  },
  blurred_match_badge_shared: {
    title: 'See your match analysis',
    body: 'Pro unlocks personalised match insights for every listing your friends share.',
  },
  cap_saved_listings: {
    title: 'Save more than 10 listings',
    body: 'Pro removes the 10-saved-listings cap so you can compare freely.',
  },
  cap_paste_a_link: {
    title: 'Add more listings',
    body: 'Pro lifts the paste-a-link cap so you can analyse every car you find.',
  },
  cap_pdf_report: {
    title: 'Generate more reports',
    body: 'Pro removes the report cap so you can export every listing you care about.',
  },
  cap_feed_listings: {
    title: 'See more matches',
    body: 'Pro removes the feed cap so you see every listing that fits your criteria.',
  },
  cap_tracked_models: {
    title: 'Track more cars',
    body: 'Pro lets you track up to 25 cars at once with full alerts and analysis.',
  },
  undo_banner: {
    title: 'Keep your matches',
    body: 'Pro brings back full access to your tracked models and saved listings.',
  },
  verdict_headline: {
    // RESERVED — UI consumer deferred to a future spec.
    title: 'See the full verdict',
    body: 'Pro unlocks the personal verdict and recommendation for every listing.',
  },
  running_costs_locked: {
    title: 'See full running costs',
    body: 'Pro unlocks fuel, insurance, and maintenance estimates personalised to you.',
  },
  cap_compare: {
    // RESERVED — Compare UI deferred per Req 15.
    title: 'Compare more cars',
    body: 'Pro lets you compare side by side without limits.',
  },
  generate_and_lock_brief: {
    // RESERVED — Negotiation paywall ships in Step 6.
    title: 'Lock in your negotiation',
    body: 'Pro lets you generate a negotiation brief and start the dialog.',
  },
  legacy_model_cap: {
    title: 'Add another car to track',
    body: 'Pro lets you track multiple models at once with alerts on each.',
  },
  weekly_digest: {
    title: 'Keep your weekly digest',
    body: 'Pro unlocks the weekly listing digest with daily price-drop alerts on every car you track.',
  },
  day_before_email: {
    title: 'Keep Pro before your trial ends',
    body: 'Lock in Pro now so your tracked models, alerts, and reports stay live.',
  },
  reality_check: {
    title: 'Keep going at the rate you set',
    body: 'Pick a plan to keep your matches, alerts, and reports.',
  },
  car_selector: {
    title: 'Skip the cap, keep them all',
    body: 'Pro lets you keep every car you tracked, not just one.',
  },
  grandfather_email_d0: {
    title: 'Founder\u2019s Rate is yours',
    body: 'As an early DooCars user you have 14 days of full Pro and a year at £4.99/month when you lock it in.',
  },
  grandfather_email_d7: {
    title: 'A week of Pro, on us',
    body: 'Keep going at this pace. Lock in £4.99/month for a year any time before day 14.',
  },
  grandfather_email_d10: {
    title: 'Your Founder\u2019s Rate window is closing',
    body: 'Lock in £4.99/month for 12 months. You have 4 days left.',
  },
  grandfather_email_d13: {
    title: 'Tomorrow is your last day at this rate',
    body: 'Lock in £4.99/month for a year now before the Founder\u2019s Rate expires.',
  },
  grandfather_email_d14: {
    title: 'Last day for the Founder\u2019s Rate',
    body: 'Today is the final day to lock in £4.99/month for a year.',
  },
  // D-018: persistent header CTA sources
  trial_header_cta: {
    title: 'Upgrade before your trial ends',
    body: 'Lock in Pro to keep your tracked models, alerts, and score breakdowns after your trial.',
  },
  grandfather_header_cta: {
    title: "Lock in your Founder's Rate",
    body: 'Upgrade now to secure the discounted rate before your Founder\'s Rate window closes.',
  },
  free_header_cta: {
    title: 'Upgrade to Pro',
    body: 'Get full access to your matches, alerts, and score breakdowns across all your cars.',
  },
  cancelled_grace_cta: {
    title: 'Keep your Pro access',
    body: 'Reactivate now to keep your tracked models, saved listings, and alerts live.',
  },
  paused_grace_cta: {
    title: 'Resume your subscription',
    body: 'Your Pro access is still active. Resume now to keep it going when the current period ends.',
  },
  paused_header_cta: {
    title: 'Resume your subscription',
    body: 'Your Pro access has lapsed. Resume to restore your tracked models and alerts.',
  },
  // D-026 mid-trial aha toasts
  trial_aha_listings_viewed_3: {
    title: 'Enjoying the hunt?',
    body: 'Keep full breakdowns on every listing past trial with Pro.',
  },
  trial_aha_dealer_click_1: {
    title: 'Going to see one?',
    body: 'Keep your scores and analysis live past trial with Pro.',
  },
  trial_aha_saves_2: {
    title: 'Comparing saved cars?',
    body: 'Pro keeps your saved list and score breakdowns after your trial.',
  },
  trial_aha_neg_2: {
    title: 'Negotiating a real deal?',
    body: 'Keep your negotiations live past trial with Pro.',
  },
  cbp_cancel_explainer: {
    title: 'Switch to a subscription',
    body: 'Keep Pro running past the end of your Car Buyer\u2019s Pass with a monthly or yearly plan.',
  },
  cbp_header_chip: {
    title: 'Your Pass is ending soon',
    body: 'Renew for another 90 days, or switch to monthly to keep Pro running without a gap.',
  },
  // J09 trial CTAs
  trial_welcome_d0: {
    title: 'Lock in Pro for after your trial',
    body: 'Upgrade now to keep your tracked models, alerts, and full match analysis when the trial ends.',
  },
  trial_nudge_d2: {
    title: 'Like what you see? Lock it in',
    body: 'Upgrade to keep Pro past your trial. No card-on-file required to try, just to stay.',
  },
  trial_midtrial: {
    title: 'Halfway through, ready to keep Pro?',
    body: 'Upgrade now to keep every match, alert, and breakdown after your trial ends.',
  },
  // J10a-e activation CTAs (for users who already have access; modal opens but
  // the entitlement gate dismisses it. PostHog attribution is the value here.)
  activation_trial: {
    title: 'Welcome to Pro',
    body: 'Open your shortlist to see every match, every breakdown, and every alert.',
  },
  activation_pastdue: {
    title: 'You are back on Pro',
    body: 'Card retry succeeded. Pick up where you left off with every Pro feature live again.',
  },
  activation_resume: {
    title: 'Pro is live again',
    body: 'Welcome back. Your tracked models, saved listings, and alerts are all running.',
  },
  activation_cbp: {
    title: 'Your Car Buyer’s Pass is live',
    body: 'Full Pro access for the next 90 days. Open your shortlist to start exploring.',
  },
  activation_direct: {
    title: 'Welcome to Pro',
    body: 'Open your shortlist to see every match, every breakdown, and every alert.',
  },
  // Negotiation in-chat upgrade triggers (audit C5)
  negotiation_generate_draft: {
    title: 'Pro generates the draft',
    body: 'Upgrade to draft the dealer message and let DooCars run the negotiation for you.',
  },
  negotiation_generate_draft_retry: {
    title: 'Pro can rewrite this',
    body: 'Upgrade to regenerate drafts up to 5 times per round until the message is right.',
  },
  negotiation_handoff: {
    title: 'Pro takes the conversation forward',
    body: 'Upgrade so DooCars can email the dealer, parse replies, and bring you a deal.',
  },
  default: {
    title: 'Unlock with Pro',
    body: 'Upgrade to Pro to unlock this feature.',
  },
};
